import Decimal from 'decimal.js';
import { Numeric } from '@/lib/types/common';
import { KeysOfType, NonEmptyArray } from '@/lib/types/helpers';

type NumSelector<T> = ((item: T) => Numeric) | KeysOfType<T, Numeric>;

/** Floor to `decimals` places. Quantities are matched on a fixed grid, so an
 *  estimate that rounds up promises shares the engine will never fill. */
export function floorTo(value: Numeric, decimals: number): Decimal {
  return new Decimal(value).toDecimalPlaces(decimals, Decimal.ROUND_DOWN);
}

export function minBy<T>(items: [], selector: NumSelector<T>): undefined;
export function minBy<T>(items: NonEmptyArray<T>, selector: NumSelector<T>): T;
export function minBy<T>(items: T[], selector: NumSelector<T>): T | undefined;
export function minBy<T>(items: T[], selector: NumSelector<T>): T | undefined {
  if (items.length === 0) {
    return undefined;
  }
  const withNumItems: { value: T; num: Decimal }[] = items.map((item) => {
    return {
      value: item,
      num:
        typeof selector === 'function'
          ? Decimal(selector(item))
          : Decimal(item[selector] as Numeric),
    };
  });
  let curMax = Decimal(Infinity);
  let minItem: T | undefined = undefined;
  for (const item of withNumItems) {
    if (Decimal(item.num).lt(curMax)) {
      curMax = item.num;
      minItem = item.value;
    }
  }

  return minItem;
}

export function maxBy<T>(items: [], selector: NumSelector<T>): undefined;
export function maxBy<T>(items: NonEmptyArray<T>, selector: NumSelector<T>): T;
export function maxBy<T>(items: T[], selector: NumSelector<T>): T | undefined;
export function maxBy<T>(items: T[], selector: NumSelector<T>): T | undefined {
  if (items.length === 0) {
    return undefined;
  }
  const withNumItems: { value: T; num: Decimal }[] = items.map((item) => {
    return {
      value: item,
      num:
        typeof selector === 'function'
          ? Decimal(selector(item))
          : Decimal(item[selector] as Numeric),
    };
  });
  let curMax = Decimal(-Infinity);
  let maxItem: T | undefined = undefined;
  for (const item of withNumItems) {
    if (Decimal(item.num).gt(curMax)) {
      curMax = item.num;
      maxItem = item.value;
    }
  }

  return maxItem;
}
